import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { DurabilityReport, QueuedOrder } from "@/lib/offlineStore";

/**
 * The offline queue, as the UI sees it. PRODUCT CHANGE §2 (`DEVIATIONS.md`).
 *
 * **NOT persisted, on purpose**, and it is not in the redux-persist whitelist
 * in `src/store/index.ts`. The queue's durable home is IndexedDB plus the
 * localStorage backup (`src/lib/offlineStore.ts`); this slice is a projection
 * of it, refreshed from disk at boot and after every write. Persisting a copy
 * here would give the app two stores that can disagree, which is precisely the
 * class of bug that loses an order — and redux-persist's rehydrate races its
 * own 5000ms timeout on a cold boot, which is how the POS lost held orders
 * before it prefetched the blob.
 */
interface OfflineOrdersState {
  orders: QueuedOrder[];
  /** A flush is running. */
  syncing: boolean;
  /** The browser's own connectivity flag, mirrored for rendering. */
  online: boolean;
  /** What the storage layer actually guaranteed. `null` until boot asks. */
  durability: DurabilityReport | null;
  lastSyncAt: number | null;
  lastError: string | null;
}

const initialState: OfflineOrdersState = {
  orders: [],
  syncing: false,
  // `true` on the server and before the first client effect: the storefront
  // must never server-render an "you are offline" banner it then removes.
  online: true,
  durability: null,
  lastSyncAt: null,
  lastError: null,
};

const offlineOrdersSlice = createSlice({
  name: "offlineOrders",
  initialState,
  reducers: {
    setOfflineQueue(state, action: PayloadAction<QueuedOrder[]>) {
      state.orders = action.payload ?? [];
    },
    setOfflineSyncing(state, action: PayloadAction<boolean>) {
      state.syncing = action.payload;
    },
    setOnline(state, action: PayloadAction<boolean>) {
      state.online = action.payload;
    },
    setDurability(state, action: PayloadAction<DurabilityReport | null>) {
      state.durability = action.payload ?? null;
    },
    setLastSync(
      state,
      action: PayloadAction<{ at: number; error?: string | null }>,
    ) {
      state.lastSyncAt = action.payload.at;
      state.lastError = action.payload.error ?? null;
    },
  },
});

export const {
  setOfflineQueue,
  setOfflineSyncing,
  setOnline,
  setDurability,
  setLastSync,
} = offlineOrdersSlice.actions;

export default offlineOrdersSlice.reducer;
